import type { ProposedAction } from '../core/types';
import {
  isAssociationPayload,
  isObjectCreatedPayload,
  isPropertyUpdatePayload,
} from '../core/types';
import {
  isTypedCreateAction,
  projectTypedCreatePayload,
} from './projectTypedCreatePayload';

export interface SoleSubjectEntity {
  entityId: string;
  entityTypeName: string;
}

/**
 * The one entity a proposal is about — when the proposal is JUST creating or
 * updating a single entity, and nothing else.
 *
 * Returns that entity when EVERY action edits the SAME entity: typed creates
 * (`create_automation` / `create_validation_rule`), `ahoy_object_created`,
 * `ahoy_property_updated`, or an association added to / removed from that
 * entity (a stage move, a company link — the record's own fields, which the
 * preview overlay projects onto its surface exactly like a property). Anything
 * else — a deletion, an email send, an association hanging off a DIFFERENT
 * record, a second entity — disqualifies the proposal (returns `undefined`),
 * because rendering only one entity's record would hide part of what approval
 * does.
 *
 * Used by the chat's docked pane and the proposal overlay to decide between
 * the entity record surface (builder-mode style, `BuilderPanel`) and the full
 * `ProposalDetailPanel`.
 */
export function soleSubjectEntityOf(
  actions: ProposedAction[]
): SoleSubjectEntity | undefined {
  if (actions.length === 0) {
    return undefined;
  }

  let subject: SoleSubjectEntity | undefined;
  for (const action of actions) {
    const ref = editedEntityOf(action);
    if (!ref) {
      // An action that is not an edit of a nameable record: the proposal does
      // more than "just" touch one record.
      return undefined;
    }
    if (subject && subject.entityId !== ref.entityId) {
      // Two distinct entities.
      return undefined;
    }
    subject = ref;
  }
  return subject;
}

/**
 * The record an action edits: the created/updated entity, or — for an
 * association — its SOURCE (a stage move edits the deal, a company link edits
 * the contact). Undefined for any other action shape.
 */
function editedEntityOf(action: ProposedAction): SoleSubjectEntity | undefined {
  if (isTypedCreateAction(action)) {
    const projected = projectTypedCreatePayload(action);
    return projected
      ? {
          entityId: projected.entityId,
          entityTypeName: projected.entityTypeName,
        }
      : undefined;
  }
  if (
    isObjectCreatedPayload(action.payload) ||
    isPropertyUpdatePayload(action.payload) ||
    isAssociationPayload(action.payload)
  ) {
    return {
      entityId: action.payload.entity_id,
      entityTypeName: action.payload.entity_type_name,
    };
  }
  return undefined;
}
